import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Button, Card, StatusIndicator } from "../components/ui";
import { ApiUnavailableState, EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import { PlanChangeImpacts, formatPeriod } from "../components/billing";
import { useAppContext } from "../hooks/useAppContext";
import {
  api,
  apiConfigured,
  ApiError,
  type DowngradeAssessment,
  type PlanChangeDirection,
  type PublicPlanView,
  type PublicPricing,
  type SafeSubscriptionView,
} from "../lib/api";
import { formatCount } from "../lib/format";

/**
 * Checkout flow (Phase 9, section 39).
 *
 * SELECT → review order summary, effective timing and entitlement changes (from the
 * server's plan-change-review) → explicit "Continue to Stripe" → redirect to the
 * HOSTED checkout URL returned by `checkout-sessions`. On return we show a neutral
 * PROCESSING screen that POLLS the safe billing status and only reports the plan as
 * active once the SERVER-side subscription confirms it — never on the redirect alone.
 *
 * NO payment details are ever collected or shown in the dashboard; the browser holds
 * no publishable or secret key. The provider price is resolved server-side.
 */

/**
 * The heading over the plan-change impact list. It has to name the direction,
 * because the body underneath it does: a customer paying to move up a plan was
 * previously shown a bare "What changes" card whose note said their resources
 * "become read-only or paused" — downgrade copy, on the page where they were
 * about to hand over money. The direction is resolved on the server from the
 * canonical plan ladder and arrives on the assessment.
 */
const CHANGE_CARD_TITLE: Readonly<Record<PlanChangeDirection, string>> = {
  upgrade: "What changes when you upgrade",
  downgrade: "What changes when you downgrade",
  lateral: "What changes",
};

const CONFIRMED_STATES = new Set(["ACTIVE", "TRIALING", "MANUAL_ACTIVE"]);
const ISSUE_STATES = new Set(["PAST_DUE", "UNPAID", "GRACE_PERIOD"]);
const MAX_POLLS = 20; // ~1 minute at 3s; after this we show delayed-webhook guidance.
/** Ask the server to re-read the payment provider every Nth poll (~15s at 3s ticks). */
const SYNC_EVERY_N_POLLS = 5;

/**
 * Whether this poll should also ask the server to re-read the payment provider.
 *
 * The very first poll after returning from checkout always does, so a customer whose
 * provider notification was delayed or lost still gets their plan without anyone
 * activating it by hand. After that it is deliberately throttled: the re-read calls an
 * external system and is rate-limited server-side, while the cheap local status read
 * runs every tick anyway, so a notification that does arrive is picked up at once.
 */
export function shouldSyncOnPoll(tickIndex: number): boolean {
  return tickIndex % SYNC_EVERY_N_POLLS === 0;
}

export type CheckoutOutcome = "confirmed" | "action_required" | "issue" | "pending";

/** Classify a fetched subscription — the ONLY basis for reporting "active". */
export function checkoutOutcome(subscription: SafeSubscriptionView | null): CheckoutOutcome {
  if (!subscription) return "pending";
  if (CONFIRMED_STATES.has(subscription.status)) return "confirmed";
  if (ISSUE_STATES.has(subscription.status)) return "issue";
  if (subscription.status === "INCOMPLETE") return "action_required";
  return "pending";
}

/** Neutral processing screen. Reports success ONLY when the server confirms it. */
export function CheckoutProcessing({
  subscription,
  pollsElapsed,
  onRefresh,
  refreshing,
}: {
  subscription: SafeSubscriptionView | null;
  pollsElapsed: number;
  onRefresh: () => void;
  refreshing: boolean;
}): JSX.Element {
  const outcome = checkoutOutcome(subscription);

  if (outcome === "confirmed") {
    return (
      <Card title="Subscription active">
        <div style={{ display: "grid", gap: "0.6rem" }}>
          <StatusIndicator tone="ok" label={`Subscription active · ${subscription?.planCode}`} />
          <p className="muted">
            Your payment provider has confirmed the subscription, and your new plan is now in force. You
            can manage it any time from the billing area.
          </p>
          <div className="row" style={{ gap: "0.75rem" }}>
            <Link to="/app/billing">
              <Button variant="primary">Go to billing</Button>
            </Link>
            <Link to="/app/usage">
              <Button variant="ghost">View usage</Button>
            </Link>
          </div>
        </div>
      </Card>
    );
  }

  if (outcome === "issue") {
    return (
      <Card title="We're finishing setup">
        <div style={{ display: "grid", gap: "0.6rem" }}>
          <StatusIndicator tone="warn" label="Payment needs attention" />
          <p className="muted">
            The subscription was created, but the payment needs attention. You can resolve it securely in
            the customer portal — no card details are handled here.
          </p>
          <div className="row" style={{ gap: "0.75rem" }}>
            <Link to="/app/billing">
              <Button variant="secondary">Go to billing</Button>
            </Link>
          </div>
        </div>
      </Card>
    );
  }

  if (outcome === "action_required") {
    return (
      <Card title="One more step at your bank">
        <div style={{ display: "grid", gap: "0.6rem" }}>
          <StatusIndicator tone="warn" label="Additional confirmation required" />
          <p className="muted">
            Your bank may need to confirm this payment (for example, a 3-D Secure check). Once that is
            done, this page will update. You can also manage the payment in the customer portal.
          </p>
          <div className="row" style={{ gap: "0.75rem" }}>
            <Button variant="secondary" onClick={onRefresh} disabled={refreshing}>
              {refreshing ? "Checking…" : "Check again"}
            </Button>
            <Link to="/app/billing">
              <Button variant="ghost">Go to billing</Button>
            </Link>
          </div>
        </div>
      </Card>
    );
  }

  // pending — still confirming. Calm, honest, NO countdown, NO false claim of success.
  const delayed = pollsElapsed >= MAX_POLLS;
  return (
    <Card title="Confirming your subscription">
      <div style={{ display: "grid", gap: "0.6rem" }} aria-live="polite">
        <div className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
          <span className="spinner" aria-hidden="true" />
          <span className="muted">We're confirming your subscription with the payment provider.</span>
        </div>
        <p className="muted">
          This can take a few moments. Your plan becomes active only once our system receives confirmation
          from the provider — we never assume it from the redirect alone. It is safe to stay on this page.
        </p>
        {delayed ? (
          <p className="muted">
            Confirmation is taking longer than usual. This is sometimes just a delayed provider
            notification. Your payment is safe. If it does not update shortly, please{" "}
            <Link to="/app/support">contact support</Link> and we will confirm it for you — you will not be
            charged twice.
          </p>
        ) : null}
        <div className="row" style={{ gap: "0.75rem" }}>
          <Button variant="secondary" onClick={onRefresh} disabled={refreshing}>
            {refreshing ? "Checking…" : "Check now"}
          </Button>
          <Link to="/app/billing">
            <Button variant="ghost">Go to billing</Button>
          </Link>
        </div>
      </div>
    </Card>
  );
}

/** The cancelled-checkout screen — no guilt, a clear way back. */
export function CheckoutCancelled(): JSX.Element {
  return (
    <Card title="Checkout cancelled">
      <div style={{ display: "grid", gap: "0.6rem" }}>
        <p className="muted">
          No problem — nothing was charged and your current plan is unchanged. You can pick a plan whenever
          you are ready.
        </p>
        <div className="row" style={{ gap: "0.75rem" }}>
          <Link to="/app/billing/plans">
            <Button variant="secondary">Back to plans</Button>
          </Link>
          <Link to="/app/billing">
            <Button variant="ghost">Billing overview</Button>
          </Link>
        </div>
      </div>
    </Card>
  );
}

/** SELECT phase: order summary + effective timing + entitlement changes. */
export function CheckoutSelect({
  plan,
  period,
  periods,
  onPeriodChange,
  assessment,
  onContinue,
  starting,
  startError,
}: {
  plan: PublicPlanView;
  period: string;
  periods: string[];
  onPeriodChange: (period: string) => void;
  assessment: DowngradeAssessment | null;
  onContinue: () => void;
  starting: boolean;
  startError: { code: string; message: string } | null;
}): JSX.Element {
  const price = plan.prices.find((p) => p.period === period);
  // BALDER allowance surfaced from the server's public limits — never invented here.
  const balderLimit = plan.primaryLimits.find((l) => /balder/i.test(l.label));
  const upgradeGuidance =
    startError && (startError.message.toLowerCase().includes("active subscription") || startError.code === "conflict");
  const unauthorized = startError?.code === "forbidden";
  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <Card title={`Order summary — ${plan.displayName}`}>
        <div style={{ display: "grid", gap: "0.6rem" }}>
          {periods.length > 1 ? (
            <label className="field" style={{ maxWidth: "16rem" }}>
              <span className="field__label">Billing period</span>
              <select className="field__input" value={period} onChange={(e) => onPeriodChange(e.target.value)}>
                {periods.map((p) => (
                  <option key={p} value={p}>
                    {formatPeriod(p).replace("per ", "")}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <div className="kv-grid">
            <div className="kv">
              <span className="kv__k">Plan</span>
              <span className="kv__v">{plan.displayName}</span>
            </div>
            <div className="kv">
              <span className="kv__k">Price</span>
              <span className="kv__v">
                {price ? `${price.formatted} ${price.currency} ${formatPeriod(period)}` : "Contact us"}
              </span>
            </div>
            <div className="kv">
              <span className="kv__k">VAT</span>
              <span className="kv__v">{plan.taxNote}</span>
            </div>
            <div className="kv">
              <span className="kv__k">Billing</span>
              <span className="kv__v">{formatPeriod(period).replace(/^per /, "Every ")}, until cancelled</span>
            </div>
            {balderLimit ? (
              <div className="kv">
                <span className="kv__k">BALDER AI usage</span>
                <span className="kv__v">{formatCount(balderLimit.value)} requests / month included</span>
              </div>
            ) : null}
            <div className="kv">
              <span className="kv__k">Trial</span>
              <span className="kv__v">{plan.trialAvailable ? "Available" : "None"}</span>
            </div>
          </div>

          <p className="muted">
            Effective timing: an upgrade applies as soon as the provider confirms payment. A change from a
            paid plan keeps your current plan until the end of the paid period. Your plan then renews
            automatically each period until you cancel. {plan.taxNote}
          </p>
          <p className="muted">
            Your plan includes a limited monthly BALDER AI allowance. Additional AI usage is never charged
            without your approval — ODIN shows the usage and price first.
          </p>
          <p className="muted">{plan.cancellationSummary}</p>
        </div>
      </Card>

      {assessment ? (
        <Card title={CHANGE_CARD_TITLE[assessment.direction]}>
          <PlanChangeImpacts assessment={assessment} />
        </Card>
      ) : null}

      {startError ? (
        <div className="banner-item banner-item--warn" role="status">
          <div className="banner-item__message" style={{ display: "grid", gap: "0.3rem" }}>
            <strong className="banner-item__label">Checkout could not start</strong>
            <p style={{ margin: 0 }}>{startError.message}</p>
            {upgradeGuidance ? (
              <p style={{ margin: 0 }} className="muted">
                You already have an active subscription. Use the customer portal from the billing overview
                to change plans.
              </p>
            ) : null}
            {unauthorized ? (
              <p style={{ margin: 0 }} className="muted">
                Only an owner or billing admin can start checkout for this workspace.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="row" style={{ gap: "0.75rem" }}>
        <Button variant="primary" onClick={onContinue} disabled={starting || !price}>
          {starting ? "Preparing secure checkout…" : "Continue to Stripe"}
        </Button>
        <Link to="/app/billing/plans">
          <Button variant="ghost">Back to plans</Button>
        </Link>
      </div>
      <p className="muted">
        Payment is handled entirely by our payment provider on their secure hosted page. Card details are
        never entered into or stored by this dashboard.
      </p>
    </div>
  );
}

type Phase = "select" | "processing" | "cancelled";

type SelectData = { pricing: PublicPricing; subscription: SafeSubscriptionView };

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; data: SelectData }
  | { kind: "unavailable"; detail?: string };

function initialPhase(param: string | null): Phase {
  if (param === "cancelled" || param === "cancel") return "cancelled";
  if (param === "processing" || param === "success" || param === "return") return "processing";
  return "select";
}

/** Return screen after a one-time usage-pack checkout (success or cancel). */
export function PackReturn({ cancelled }: { cancelled: boolean }): JSX.Element {
  if (cancelled) {
    return (
      <Card title="Checkout cancelled">
        <div style={{ display: "grid", gap: "0.6rem" }}>
          <p className="muted">No problem — nothing was charged and your usage is unchanged.</p>
          <div className="row" style={{ gap: "0.75rem" }}>
            <Link to="/app/usage">
              <Button variant="secondary">Back to usage</Button>
            </Link>
          </div>
        </div>
      </Card>
    );
  }
  return (
    <Card title="Extra builds on the way">
      <div style={{ display: "grid", gap: "0.6rem" }}>
        <StatusIndicator tone="ok" label="Payment received" />
        <p className="muted">
          Thanks! Your extra BALDER builds are added to this billing period as soon as our system receives
          confirmation from the payment provider — usually within a moment. You'll see the higher allowance
          on your usage page.
        </p>
        <div className="row" style={{ gap: "0.75rem" }}>
          <Link to="/app/usage">
            <Button variant="primary">View usage</Button>
          </Link>
        </div>
      </div>
    </Card>
  );
}

export function Checkout(): JSX.Element {
  const { tenantId } = useAppContext();
  const [searchParams] = useSearchParams();
  const isPack = searchParams.get("kind") === "pack";
  const phase = initialPhase(searchParams.get("phase") ?? searchParams.get("checkout"));

  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [period, setPeriod] = useState<string>("monthly");
  const [assessment, setAssessment] = useState<DowngradeAssessment | null>(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<{ code: string; message: string } | null>(null);

  // Processing-phase polling state.
  const [subscription, setSubscription] = useState<SafeSubscriptionView | null>(null);
  const [pollsElapsed, setPollsElapsed] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const requestedPlan = searchParams.get("plan");

  const loadSelect = useCallback(() => {
    if (!apiConfigured || !tenantId) return;
    setState({ kind: "loading" });
    Promise.all([api.billing.plans(), api.billing.subscription(tenantId)])
      .then(([pricing, sub]) => setState({ kind: "ready", data: { pricing, subscription: sub.subscription } }))
      .catch((error: unknown) =>
        setState({ kind: "unavailable", detail: error instanceof Error ? error.message : undefined }),
      );
  }, [tenantId]);

  useEffect(() => {
    if (phase === "select") loadSelect();
  }, [phase, loadSelect]);

  const selectedPlan: PublicPlanView | null = useMemo(() => {
    if (state.kind !== "ready") return null;
    const plans = state.data.pricing.plans.filter((p) => p.planCode !== "FREE" && p.billingAvailable && p.prices.length > 0);
    return plans.find((p) => p.planCode === requestedPlan) ?? plans[0] ?? null;
  }, [state, requestedPlan]);

  // Default the period to the plan's first supported (cheaper) period — never the
  // most expensive by default.
  useEffect(() => {
    const first = selectedPlan?.prices[0];
    if (selectedPlan && first) {
      setPeriod((prev) => (selectedPlan.prices.some((p) => p.period === prev) ? prev : first.period));
    }
  }, [selectedPlan]);

  // Fetch the non-destructive change assessment for the selected plan.
  useEffect(() => {
    if (phase !== "select" || !tenantId || !selectedPlan) return;
    let active = true;
    api.billing
      .planChangeReview(tenantId, selectedPlan.planCode)
      .then((res) => {
        if (active) setAssessment(res.assessment);
      })
      .catch(() => {
        if (active) setAssessment(null);
      });
    return () => {
      active = false;
    };
  }, [phase, tenantId, selectedPlan]);

  const onContinue = useCallback(async () => {
    if (!tenantId || !selectedPlan) return;
    setStarting(true);
    setStartError(null);
    try {
      const result = await api.billing.startCheckout(tenantId, { planCode: selectedPlan.planCode, period });
      // Redirect the browser to the HOSTED checkout URL. No card data is handled here.
      window.location.assign(result.checkoutUrl);
    } catch (error) {
      if (error instanceof ApiError) {
        setStartError({ code: error.code, message: error.message });
      } else {
        setStartError({ code: "error", message: "Checkout could not be started. Please try again shortly." });
      }
      setStarting(false);
    }
  }, [tenantId, selectedPlan, period]);

  // Poll the safe subscription status in the processing phase until confirmed.
  const pollRef = useRef<number | null>(null);
  const tickRef = useRef(0);

  /**
   * Read the safe subscription status, optionally asking the server to re-read the
   * payment provider first.
   *
   * The re-read exists because a provider notification can be delayed or lost, and a
   * customer who has paid must not have to wait for a human to activate their plan.
   * It is READ-ONLY at the provider and resolves the customer server-side; the
   * browser sends no plan, price or customer. A failure is deliberately swallowed —
   * the caller may lack the billing capability, or be rate-limited — because the
   * status read that follows still reports the truth.
   */
  const refresh = useCallback(
    async (options: { sync?: boolean } = {}) => {
      if (!tenantId) return;
      setRefreshing(true);
      try {
        if (options.sync) await api.billing.sync(tenantId).catch(() => undefined);
        const res = await api.billing.subscription(tenantId);
        setSubscription(res.subscription);
      } catch {
        // Keep the calm processing screen; a transient error is not a failure.
      } finally {
        setRefreshing(false);
      }
    },
    [tenantId],
  );

  /** An explicit "check now" click always re-reads the provider. */
  const onManualRefresh = useCallback(() => {
    void refresh({ sync: true });
  }, [refresh]);

  useEffect(() => {
    if (phase !== "processing" || !apiConfigured || !tenantId) return;
    let cancelled = false;
    tickRef.current = 0;
    const tick = async () => {
      if (cancelled) return;
      const n = tickRef.current;
      tickRef.current = n + 1;
      await refresh({ sync: shouldSyncOnPoll(n) });
      setPollsElapsed((n2) => n2 + 1);
    };
    void tick();
    pollRef.current = window.setInterval(() => {
      setSubscription((current) => {
        // Stop polling once confirmed.
        if (current && checkoutOutcome(current) === "confirmed") {
          if (pollRef.current !== null) window.clearInterval(pollRef.current);
          return current;
        }
        void tick();
        return current;
      });
    }, 3000);
    return () => {
      cancelled = true;
      if (pollRef.current !== null) window.clearInterval(pollRef.current);
    };
  }, [phase, tenantId, refresh]);

  if (!apiConfigured) {
    return (
      <>
        <PageHeader title="Checkout" />
        <NotConfiguredState title="Control API URL not configured">
          Set <code>PUBLIC_API_URL</code> when building the dashboard to start checkout.
        </NotConfiguredState>
      </>
    );
  }

  if (!tenantId) {
    return (
      <>
        <PageHeader title="Checkout" />
        <EmptyState title="Select a workspace">Choose a workspace before starting checkout.</EmptyState>
      </>
    );
  }

  // One-time usage-pack return (marked with ?kind=pack). Distinct from subscription
  // checkout: there is no subscription to poll — the credit is applied by the webhook.
  if (isPack && (phase === "processing" || phase === "cancelled")) {
    return (
      <>
        <PageHeader title="Usage pack" />
        <PackReturn cancelled={phase === "cancelled"} />
      </>
    );
  }

  if (phase === "cancelled") {
    return (
      <>
        <PageHeader title="Checkout" />
        <CheckoutCancelled />
      </>
    );
  }

  if (phase === "processing") {
    return (
      <>
        <PageHeader title="Checkout" />
        <CheckoutProcessing
          subscription={subscription}
          pollsElapsed={pollsElapsed}
          onRefresh={onManualRefresh}
          refreshing={refreshing}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Checkout">
        <p>Review your order, then continue to our payment provider's secure page.</p>
      </PageHeader>
      {state.kind === "loading" ? <LoadingState label="Loading checkout…" /> : null}
      {state.kind === "unavailable" ? (
        <div className="state" role="alert">
          <ApiUnavailableState detail={state.detail} />
          <Button variant="secondary" onClick={loadSelect}>
            Retry
          </Button>
        </div>
      ) : null}
      {state.kind === "ready" && !selectedPlan ? (
        <EmptyState title="No purchasable plan selected">
          Choose a plan from the <Link to="/app/billing/plans">plan comparison</Link> to continue.
        </EmptyState>
      ) : null}
      {state.kind === "ready" && selectedPlan ? (
        <CheckoutSelect
          plan={selectedPlan}
          period={period}
          periods={selectedPlan.prices.map((p) => p.period)}
          onPeriodChange={setPeriod}
          assessment={assessment}
          onContinue={onContinue}
          starting={starting}
          startError={startError}
        />
      ) : null}
    </>
  );
}
